import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useOrg } from "../auth/OrgProvider";
import { Card, CardHeader } from "../ui/Card";
import type { Customer } from "@lawnops/shared";

export function CustomersPage() {
  const { t } = useTranslation("customers");
  const { activeOrgId } = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ["customers", activeOrgId],
    enabled: Boolean(supabase && activeOrgId),
    queryFn: async (): Promise<Customer[]> => {
      if (!supabase || !activeOrgId) return [];
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("organization_id", activeOrgId)
        .is("archived_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Customer[];
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("title")}</h1>
          <p className="text-sm text-slate-500">{t("subtitle")}</p>
        </div>
      </div>
      <Card>
        <CardHeader title={t("listTitle")} />
        {!supabase ? (
          <EmptyState message={t("supabaseEmpty")} />
        ) : isLoading ? (
          <EmptyState message={t("loading")} />
        ) : error ? (
          <EmptyState message={`${t("errorPrefix")}${(error as Error).message}`} />
        ) : data && data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {data.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.email ?? c.phone ?? "—"}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState message={t("empty")} />
        )}
      </Card>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{message}</p>;
}
